import React, { useEffect, useRef, useState } from 'react';
import { Truck, Warehouse, Globe, Container, Package, Snowflake, ArrowRight } from 'lucide-react';
import { services } from '../utils/mock';

const iconMap = {
  truck: Truck,
  warehouse: Warehouse,
  globe: Globe,
  container: Container,
  package: Package,
  snowflake: Snowflake
};

const Services = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardsRef.current.indexOf(entry.target);
            if (index !== -1) {
              setVisibleCards((prev) => {
                if (!prev.includes(index)) {
                  return [...prev, index];
                }
                return prev;
              });
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-20 md:py-32 relative overflow-hidden">
      {/* Clean Truck with Trailer Background - Light & Blurred for Easy Reading */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/services-clean-truck.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            filter: 'blur(4px) brightness(1.4) contrast(0.7)'
          }}
        ></div>
        {/* Strong Light Overlay for Better Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/97 via-[#FFFBF7]/98 to-white/96"></div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#E6A05C]/8 to-transparent rounded-full filter blur-3xl z-0"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#D4894B]/8 to-transparent rounded-full filter blur-3xl z-0"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#E6A05C]/10 rounded-full px-6 py-2 mb-6">
            <span className="w-2 h-2 bg-[#E6A05C] rounded-full"></span>
            <span className="text-[#E6A05C] font-semibold text-sm uppercase tracking-wide">Our Services</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0D2436] mb-6">
            Comprehensive Transportation Solutions
          </h2>
          <p className="text-lg text-gray-600">
            From coast to coast and across borders, we provide reliable logistics services tailored to your needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            const isVisible = visibleCards.includes(index);
            
            return (
              <div
                key={service.id}
                ref={(el) => (cardsRef.current[index] = el)}
                className={`group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-gray-100 hover:border-[#E6A05C]/50 transform hover:-translate-y-3 hover:scale-105 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#E6A05C] to-[#D4894B] rounded-xl flex items-center justify-center group-hover:scale-125 group-hover:rotate-6 transition-all duration-300 shadow-lg">
                    <Icon className="text-white" size={32} />
                  </div>
                  <div className="absolute top-0 left-0 w-16 h-16 bg-[#E6A05C] rounded-xl blur-xl opacity-0 group-hover:opacity-60 transition-opacity duration-300"></div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-[#0D2436] mb-4 group-hover:text-[#E6A05C] transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-sm text-gray-700">
                      <div className="w-1.5 h-1.5 bg-[#E6A05C] rounded-full"></div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Learn More Button */}
                <button className="group/btn flex items-center space-x-2 text-[#E6A05C] font-semibold hover:text-[#D4894B] transition-colors duration-200">
                  <span>Learn More</span>
                  <ArrowRight className="group-hover/btn:translate-x-1 transition-transform duration-200" size={18} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-black-600 mb-6">
            Need a custom logistics solution? We're here to help.
          </p>
          <button 
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#0D2436] text-[#F5E6D3] px-8 py-4 rounded-lg font-semibold hover:bg-[#1B3A52] transition-all duration-300 hover:scale-105 hover:shadow-xl inline-flex items-center space-x-2"
          >
            <span>Get Custom Quote</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
